import { FormInput } from "@general-backoffice/core/index";
import React, { useEffect, useMemo } from "react";

import useExchanges from "./hooks/useExchanges";

interface ExchangeOption {
  id: number | string;
  name: string;
  leverageOptionsConstraint?: string;
}

interface ExchangeSelectProps {
  exchange: ExchangeOption | { id: number | string; name?: string; [key: string]: unknown } | null;
  leverage?: number;
  setExchange: (exchange: ExchangeOption | null) => void;
  isDisabled?: boolean;
  label?: string;
  required?: boolean;
  description?: React.ReactNode;
  [key: string]: unknown;
}

const ExchangeSelect = ({ exchange, leverage, setExchange, ...props }: ExchangeSelectProps) => {
  const { exchanges, isLoading } = useExchanges();

  // Filter exchanges based on leverage option selected
  const leverageAvailableExchanges = useMemo(() => {
    if (!exchanges?.length) return [];
    if (!leverage) return exchanges;
    return exchanges.filter(({ leverageOptionsConstraint }) => {
      if (!leverageOptionsConstraint) return true;
      if (leverageOptionsConstraint === "only_30" && leverage <= 30) return true;
      if (leverageOptionsConstraint === "only_1k" && leverage > 30) return true;
      return false;
    });
  }, [exchanges, leverage]);

  const options = useMemo(
    () =>
      leverageAvailableExchanges.map((exchange) => ({
        value: exchange.id,
        label: exchange.name,
        data: exchange,
      })),
    [leverageAvailableExchanges]
  );

  const currOption = useMemo(
    () => exchange && options.find(({ value }) => `${value}` === `${exchange.id}`),
    [options, exchange]
  );

  // Check if the current option is valid
  useEffect(() => {
    if (!!leverage && !currOption && exchange) setExchange(null);
  }, [currOption, exchange, leverage, setExchange]);

  return (
    <FormInput.Select
      // label="Broker"
      label="Bróker"
      {...props}
      onChange={(option) => setExchange(option?.data || null)}
      options={options}
      value={currOption}
      isDisabled={isLoading || props.isDisabled}
      isClearable
    />
  );
};

export default ExchangeSelect;
